import { useEffect, useState } from 'react';

import Browse from './Browse.jsx';
import Player from './Player.jsx';
import { fetchVideo, fetchVideos } from './api.js';
import { formatBytes, formatDuration, formatResolution } from './format.js';

function Link({ to, className, children }) {
  return (
    <a
      className={className}
      href={to}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        navigate(to);
      }}
    >
      {children}
    </a>
  );
}

function navigate(to) {
  if (to === window.location.pathname) return;
  window.history.pushState(null, '', to);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/** Real URLs so a video page can be copied and shared, without pulling in a router. */
function usePathname() {
  const [pathname, setPathname] = useState(() => window.location.pathname);

  useEffect(() => {
    const onPop = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  return pathname;
}

function useAsync(loader, deps) {
  const [state, setState] = useState({ status: 'loading', data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', data: null, error: null });

    loader()
      .then((data) => !cancelled && setState({ status: 'ready', data, error: null }))
      .catch((error) => !cancelled && setState({ status: 'error', data: null, error }));

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return state;
}

function Badges({ video }) {
  return (
    <span className="badges">
      {video.maxHeight && <span className="badge badge--res">{formatResolution(video.maxHeight)}</span>}
      {video.codecs.includes('hevc') && <span className="badge">HEVC</span>}
      {video.duration != null && <span className="badge badge--quiet">{formatDuration(video.duration)}</span>}
    </span>
  );
}

function Library() {
  const { status, data, error } = useAsync(fetchVideos, []);

  if (status === 'loading') return <p className="msg">Loading library…</p>;
  if (status === 'error') return <p className="msg msg--bad">Could not load library: {error.message}</p>;

  if (data.length === 0) {
    return (
      <div className="msg">
        <p>No videos yet.</p>
        <p className="msg__hint">
          Drop an encoded HLS tree into <code>media/&lt;id&gt;/</code> with a <code>master.m3u8</code> at
          its root. It will appear here within a second.
        </p>
      </div>
    );
  }

  return (
    <ul className="grid">
      {data.map((video) => (
        <li key={video.id}>
          <Link className="card" to={`/${video.id}`}>
            <div className="card__art">
              {video.poster ? (
                <img src={video.poster} alt="" loading="lazy" decoding="async" />
              ) : (
                <span className="card__art-empty" aria-hidden="true" />
              )}
            </div>
            <div className="card__body">
              <h2 className="card__title">{video.title}</h2>
              <Badges video={video} />
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function VideoPage({ id }) {
  const { status, data, error } = useAsync(() => fetchVideo(id), [id]);

  if (status === 'loading') return <p className="msg">Loading…</p>;
  if (status === 'error') return <p className="msg msg--bad">Not found: {error.message}</p>;

  return (
    <article className="watch">
      <Player video={data} autoPlay />
      <header className="watch__head">
        <h1>{data.title}</h1>
        <Badges video={data} />
      </header>
      {data.description && <p className="watch__desc">{data.description}</p>}
      <dl className="facts">
        <div>
          <dt>Renditions</dt>
          <dd>{data.variants}</dd>
        </div>
        <div>
          <dt>Codecs</dt>
          <dd>{data.codecs.join(', ') || '—'}</dd>
        </div>
        <div>
          <dt>On disk</dt>
          <dd>{formatBytes(data.sizeBytes) ?? '—'}</dd>
        </div>
      </dl>
    </article>
  );
}

const VIDEO_ROUTE = /^\/([a-z][0-9a-z]{23})\/?$/;

export default function App() {
  const pathname = usePathname();
  const match = VIDEO_ROUTE.exec(pathname);

  return (
    <div className="app">
      <header className="app__head">
        <Link className="app__brand" to="/">
          FastVid
        </Link>
        {/* Hidden while watching: a shared video link should open on the video,
            not advertise the rest of the library to whoever received it. */}
        {/* `sms:?&body=` is the form both iOS and Android Messages accept. */}
        {match ? (
          <nav className="app__nav">
            <a className="app__link" href={`sms:?&body=${encodeURIComponent(window.location.href)}`}>
              Share via SMS
            </a>
          </nav>
        ) : (
          <nav className="app__nav">
            <Link className={`app__link${pathname === '/' ? ' app__link--on' : ''}`} to="/">
              Library
            </Link>
            <Link
              className={`app__link${pathname === '/browse' ? ' app__link--on' : ''}`}
              to="/browse"
            >
              Browse
            </Link>
          </nav>
        )}
      </header>

      <main className="app__main">
        {match ? <VideoPage id={match[1]} /> : pathname === '/browse' ? <Browse /> : <Library />}
      </main>
    </div>
  );
}
